<template>
  <BaseModal
    v-model="isVisible"
    title="Current Estimate"
    :loading="isLoading"
    @close="handleClose"
    size="xl"
  >
    <div v-if="currentEstimate && items.length > 0" class="current-estimate-content">
      <!-- Items Table -->
      <div class="items-table-section">
        <table class="items-table">
          <thead>
            <tr>
              <th>Part No</th>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Line Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td class="part-no-cell">{{ item.part_no }}</td>
              <td class="description-cell">{{ item.description || 'N/A' }}</td>
              <td class="quantity-cell">
                <div class="quantity-controls">
                  <button
                    class="quantity-btn decrease-btn"
                    @click="decreaseQuantity(item)"
                    :disabled="isUpdating || item.quantity <= getMinimumQuantity(item)"
                    :title="item.quantity <= getMinimumQuantity(item) ? `Minimum quantity is ${getMinimumQuantity(item)}` : 'Decrease quantity'"
                  >
                    <font-awesome-icon icon="fa-solid fa-minus" />
                  </button>
                  <input
                    :value="item.quantity"
                    type="number"
                    :min="getMinimumQuantity(item)"
                    class="quantity-input"
                    @input="handleQuantityInput($event, item)"
                    @blur="handleQuantityBlur($event, item)"
                    :disabled="isUpdating"
                    :title="`Minimum: ${getMinimumQuantity(item)} units`"
                  />
                  <button
                    class="quantity-btn increase-btn"
                    @click="increaseQuantity(item)"
                    :disabled="isUpdating"
                    title="Increase quantity"
                  >
                    <font-awesome-icon icon="fa-solid fa-plus" />
                  </button>
                </div>
              </td>
              <td class="price-cell">{{ format(item.unit_price) }}</td>
              <td class="total-cell">{{ format(item.line_total) }}</td>
              <td class="actions-cell">
                <button
                  class="remove-btn"
                  @click="handleRemoveItem(item.id)"
                  :disabled="isRemoving"
                  title="Remove item"
                >
                  <font-awesome-icon icon="fa-solid fa-trash" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Summary Section -->
      <div class="summary-section">
        <div class="summary-row">
          <span class="summary-label">Total Items:</span>
          <span class="summary-value">{{ itemCount }}</span>
        </div>
        <div class="summary-row total">
          <span class="summary-label">Total Price:</span>
          <span class="summary-value">{{ format(totalPrice) }}</span>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>

    <div v-else-if="!isLoading" class="empty-estimate">
      <div class="empty-icon">
        <font-awesome-icon icon="fa-solid fa-cart-shopping" />
      </div>
      <p>Your estimate is empty</p>
      <p class="empty-subtitle">Add items from the QuickSpire page to get started</p>
    </div>

    <div v-else class="loading-estimate">
      <p>Loading estimate...</p>
    </div>

    <template #footer>
      <div class="modal-actions">
        <button type="button" class="btn btn-secondary" @click="handleClose" :disabled="isSaving">
          Close
        </button>
        <button
          v-if="items.length > 0"
          type="button"
          class="btn btn-primary"
          @click="handleSaveEstimate"
          :disabled="isSaving || items.length === 0"
        >
          <span v-if="isSaving">Saving...</span>
          <span v-else>Save Estimate</span>
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { ref, computed, watch } from 'vue'
import BaseModal from '@/components/ui/BaseModal.vue'
import { useEstimates } from '@/composables/useEstimates'
import { useCurrency } from '@/composables/useCurrency'

export default {
  name: 'CurrentEstimateModal',
  components: {
    BaseModal
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'close', 'saved'],
  setup(props, { emit }) {
    const {
      currentEstimate,
      fetchCurrentEstimate,
      removeItemFromEstimate,
      updateEstimateItemQuantity,
      saveEstimate
    } = useEstimates()

    const isVisible = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })

    const isLoading = ref(false)
    const isRemoving = ref(false)
    const isSaving = ref(false)
    const isUpdating = ref(false)
    const errorMessage = ref(null)
    const debounceTimers = ref({})

    const items = computed(() => {
      const itemsList = currentEstimate.value?.items || []

      // Sort by part_no (ascending), then by quantity (ascending)
      return [...itemsList].sort((a, b) => {
        // First, compare part_no
        if (a.part_no < b.part_no) return -1
        if (a.part_no > b.part_no) return 1

        // If part_no is the same, compare quantity
        return a.quantity - b.quantity
      })
    })

    const itemCount = computed(() => {
      return currentEstimate.value?.item_count || 0
    })

    const totalPrice = computed(() => {
      return currentEstimate.value?.total_price || '0.00'
    })

    const { format } = useCurrency()

    const getMinimumQuantity = (item) => {
      if (!item.pricing_tier_info || item.pricing_tier_info.length === 0) {
        return 1 // Default to 1 if no pricing tiers
      }

      // Find the lowest minimumQty from all pricing tiers
      const minQuantities = item.pricing_tier_info.map(tier =>
        tier.minimumQty || tier.minimum_qty || 0
      )

      return Math.min(...minQuantities) || 1
    }

    const updateQuantity = async (item, newQuantity) => {
      // Store original quantity for reverting on error
      const originalQuantity = item.quantity

      isUpdating.value = true
      errorMessage.value = null

      try {
        const response = await updateEstimateItemQuantity(item.id, newQuantity)

        if (!response.success) {
          // Revert quantity on error
          item.quantity = originalQuantity
          errorMessage.value = response.message || 'Failed to update quantity'
        }
      } catch (error) {
        console.error('Error updating quantity:', error)
        // Revert quantity on error
        item.quantity = originalQuantity
        errorMessage.value = 'An error occurred while updating quantity'
      } finally {
        isUpdating.value = false
      }
    }

    const decreaseQuantity = (item) => {
      const minQty = getMinimumQuantity(item)
      if (item.quantity > minQty) {
        updateQuantity(item, item.quantity - 1)
      }
    }

    const increaseQuantity = (item) => {
      updateQuantity(item, item.quantity + 1)
    }

    const handleQuantityInput = (event, item) => {
      const newQuantity = parseInt(event.target.value)

      // Clear error message while typing to allow free input
      errorMessage.value = null

      // Clear existing timer for this item
      if (debounceTimers.value[item.id]) {
        clearTimeout(debounceTimers.value[item.id])
      }

      // Set new timer for API call
      debounceTimers.value[item.id] = setTimeout(() => {
        const minQty = getMinimumQuantity(item)
        // Only call API if quantity is valid, but don't show error
        // If invalid, do nothing - validation will happen on blur
        if (!isNaN(newQuantity) && newQuantity >= minQty) {
          updateQuantity(item, newQuantity)
        }
      }, 500)
    }

    const handleQuantityBlur = (event, item) => {
      const newQuantity = parseInt(event.target.value)
      const minQty = getMinimumQuantity(item)

      // If empty or invalid, revert to current quantity
      if (isNaN(newQuantity) || newQuantity < 1) {
        event.target.value = item.quantity
        errorMessage.value = null
        return
      }

      // If below minimum, show error and revert
      if (newQuantity < minQty) {
        errorMessage.value = `Minimum quantity is ${minQty} units`
        event.target.value = item.quantity
        // Cancel pending API call
        if (debounceTimers.value[item.id]) {
          clearTimeout(debounceTimers.value[item.id])
        }
        return
      }

      // Valid quantity - the debounce timer will handle the update
      errorMessage.value = null
    }

    const handleRemoveItem = async (itemId) => {
      if (!confirm('Are you sure you want to remove this item?')) {
        return
      }

      isRemoving.value = true
      errorMessage.value = null

      try {
        const response = await removeItemFromEstimate(itemId)

        if (!response.success) {
          errorMessage.value = response.message || 'Failed to remove item'
        }
      } catch (error) {
        console.error('Error removing item:', error)
        errorMessage.value = 'An error occurred while removing the item'
      } finally {
        isRemoving.value = false
      }
    }

    const handleSaveEstimate = async () => {
      if (items.value.length === 0) {
        return
      }

      if (!confirm('Save this estimate? It will be locked with a unique ID.')) {
        return
      }

      isSaving.value = true
      errorMessage.value = null

      try {
        const response = await saveEstimate()

        if (response.success) {
          emit('saved', response.data)
          handleClose()
        } else {
          errorMessage.value = response.message || 'Failed to save estimate'
        }
      } catch (error) {
        console.error('Error saving estimate:', error)
        errorMessage.value = 'An error occurred while saving the estimate'
      } finally {
        isSaving.value = false
      }
    }

    const handleClose = () => {
      errorMessage.value = null
      emit('close')
      emit('update:modelValue', false)
    }

    // Fetch current estimate when modal opens
    watch(isVisible, async (newValue) => {
      if (newValue) {
        isLoading.value = true
        try {
          await fetchCurrentEstimate()
        } catch (error) {
          console.error('Error fetching current estimate:', error)
          errorMessage.value = 'Failed to load estimate'
        } finally {
          isLoading.value = false
        }
      }
    })

    return {
      isVisible,
      isLoading,
      isRemoving,
      isSaving,
      isUpdating,
      errorMessage,
      currentEstimate,
      items,
      itemCount,
      totalPrice,
      format,
      getMinimumQuantity,
      decreaseQuantity,
      increaseQuantity,
      handleQuantityInput,
      handleQuantityBlur,
      handleRemoveItem,
      handleSaveEstimate,
      handleClose
    }
  }
}
</script>

<style scoped>
.current-estimate-content {
  padding: 0;
}

.items-table-section {
  overflow-x: auto;
  margin-bottom: 24px;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.items-table th {
  background-color: #f8fafc;
  padding: 12px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  border-bottom: 2px solid #e2e8f0;
}

.items-table td {
  padding: 12px;
  font-size: 14px;
  color: #1f2937;
  border-bottom: 1px solid #e2e8f0;
}

.part-no-cell {
  font-weight: 600;
  color: #3b82f6;
}

.description-cell {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quantity-cell {
  width: 160px;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
}

.quantity-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1d5db;
  background-color: #ffffff;
  color: #374151;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
}

.quantity-btn:hover:not(:disabled) {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.quantity-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f9fafb;
}

.quantity-input {
  width: 60px;
  height: 28px;
  text-align: center;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  padding: 0 8px;
}

.quantity-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.quantity-input:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.price-cell, .total-cell {
  font-weight: 600;
  color: #10b981;
}

.actions-cell {
  width: 80px;
  text-align: center;
}

.remove-btn {
  background-color: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.remove-btn:hover:not(:disabled) {
  background-color: #fef2f2;
  color: #dc2626;
}

.remove-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.summary-section {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
}

.summary-row.total {
  border-top: 2px solid #e2e8f0;
  margin-top: 12px;
  padding-top: 16px;
  font-size: 18px;
  font-weight: 700;
}

.summary-label {
  color: #6b7280;
  font-weight: 500;
}

.summary-value {
  color: #1f2937;
  font-weight: 600;
}

.summary-row.total .summary-value {
  color: #10b981;
  font-size: 24px;
}

.empty-estimate,
.loading-estimate {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.empty-icon {
  margin-bottom: 16px;
}

.empty-icon svg {
  font-size: 64px;
  color: #d1d5db;
}

.empty-estimate p {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 8px 0;
}

.empty-subtitle {
  font-size: 14px !important;
  color: #6b7280 !important;
  font-weight: 400 !important;
}

.error-message {
  margin-top: 16px;
  padding: 12px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.btn {
  padding: 12px 28px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.btn-primary {
  background-color: #3b82f6;
  color: #ffffff;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

@media (max-width: 768px) {
  .items-table {
    font-size: 12px;
  }

  .items-table th,
  .items-table td {
    padding: 8px;
  }

  .description-cell {
    max-width: 150px;
  }

  .modal-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}
</style>
